import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { X, Pen, Eraser, Minus, ArrowRight, Circle, MousePointer2, Undo2, Redo2, Trash2, Move, Keyboard, Camera, PaintBucket, ImagePlus, Check, ChevronDown, FolderOpen } from 'lucide-react';
import { registerUndoScope } from '../hooks/useUndoArbiter';

declare global {
    interface Window { fabric: any; }
}

type Tool = 'pen' | 'eraser' | 'line' | 'arrow' | 'circle' | 'select';

const COLORS = [
    { v: '#ef4444', label: 'Red' },
    { v: '#f59e0b', label: 'Yellow' },
    { v: '#ffffff', label: 'White' },
    { v: '#000000', label: 'Black' },
    { v: '#3b82f6', label: 'Blue' },
    { v: '#22c55e', label: 'Green' },
    { v: '#f97316', label: 'Orange' },
    { v: '#a855f7', label: 'Purple' },
    { v: '#ec4899', label: 'Pink' },
];

const SIZES = [3, 6, 14];

// Pen colours the user picked with "+", kept between sessions. Capped so the toolbar stays short:
// picking one more replaces the oldest.
const CUSTOM_COLORS_KEY = 'draw_custom_colors';
const MAX_CUSTOM_COLORS = 4;
function loadCustomColors(): string[] {
    try {
        const saved = JSON.parse(localStorage.getItem(CUSTOM_COLORS_KEY) || '[]');
        return Array.isArray(saved) ? saved.filter((c: unknown) => typeof c === 'string').slice(-MAX_CUSTOM_COLORS) : [];
    } catch { return []; }
}

// null = transparent (see through to the screen below)
const BG_COLORS: { v: string | null; label: string }[] = [
    { v: null, label: 'Transparent' },
    { v: '#ffffff', label: 'Whiteboard' },
    { v: '#1b4332', label: 'Chalkboard' },
    { v: '#faf3e6', label: 'Paper' },
    { v: '#eef2ff', label: 'Notebook' },
    { v: '#111827', label: 'Midnight' },
    { v: '#fef9c3', label: 'Sticky note' },
    { v: '#d1fae5', label: 'Mint' },
];

// Patterns drawn on a solid background (they show in the video and in a drawing saved to a note).
type BgPattern = 'none' | 'dots' | 'grid' | 'lines';
const BG_PATTERNS: { v: BgPattern; label: string }[] = [
    { v: 'none', label: 'Plain' },
    { v: 'dots', label: 'Dots' },
    { v: 'grid', label: 'Grid' },
    { v: 'lines', label: 'Lines' },
];
const isDarkColor = (hex: string) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
    if (!m) return false;
    const [r, g, b] = [m[1], m[2], m[3]].map(h => parseInt(h, 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
};
/** One repeating tile: the background colour plus the pattern, ink adapted to light/dark grounds. */
function makePatternTile(color: string, pattern: BgPattern): HTMLCanvasElement {
    const size = pattern === 'lines' ? 32 : 24;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const g = c.getContext('2d')!;
    g.fillStyle = color;
    g.fillRect(0, 0, size, size);
    const ink = isDarkColor(color) ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.18)';
    g.fillStyle = ink;
    g.strokeStyle = ink;
    g.lineWidth = 1;
    if (pattern === 'dots') {
        g.beginPath(); g.arc(size / 2, size / 2, 1.3, 0, Math.PI * 2); g.fill();
    } else if (pattern === 'grid') {
        g.beginPath(); g.moveTo(0.5, 0); g.lineTo(0.5, size); g.moveTo(0, 0.5); g.lineTo(size, 0.5); g.stroke();
    } else if (pattern === 'lines') {
        g.beginPath(); g.moveTo(0, size - 0.5); g.lineTo(size, size - 0.5); g.stroke();
    }
    return c;
}
// Snapshots (undo/redo, pages, saved strokes) are loaded without the background they were taken
// with: the background is a view setting re-applied after loading. Keeping it would also make a
// solid background blink — the snapshot's (often clear) background showed for a moment first.
const parseWithoutBackground = (json: string) => {
    const data = JSON.parse(json);
    delete data.background;
    delete data.backgroundImage;
    delete data.overlay;
    return data;
};
const LAST_BG_KEY = 'draw_last_solid_bg';
const BG_PATTERN_KEY = 'draw_bg_pattern';

const TOOL_ORDER: Tool[] = ['pen', 'eraser', 'line', 'arrow', 'circle', 'select'];


type HotkeyAction = 'pen' | 'eraser' | 'line' | 'arrow' | 'circle' | 'select' | 'colorNext' | 'colorPrev' | 'sizeUp' | 'sizeDown' | 'toggleOverlay' | 'savePage' | 'toggleBg';

type HotkeyMap = Record<HotkeyAction, string>;

const DEFAULT_HOTKEYS: HotkeyMap = {
    pen: 'p',
    eraser: 'e',
    line: 'l',
    arrow: 'a',
    circle: 'c',
    select: 'v',
    colorNext: ']',
    colorPrev: '[',
    sizeUp: '=',
    sizeDown: '-',
    toggleOverlay: 'd',
    savePage: 'n',
    toggleBg: 'b',
};

const ACTION_LABELS: Record<HotkeyAction, string> = {
    pen: 'Pen',
    eraser: 'Eraser',
    line: 'Line',
    arrow: 'Arrow',
    circle: 'Circle',
    select: 'Select',
    colorNext: 'Next color',
    colorPrev: 'Prev color',
    sizeUp: 'Size +',
    sizeDown: 'Size −',
    toggleOverlay: 'Toggle overlay',
    savePage: 'Save & new page',
    toggleBg: 'Solid / clear background',
};

function loadHotkeys(): HotkeyMap {
    try {
        const saved = localStorage.getItem('draw_hotkeys');
        if (saved) return { ...DEFAULT_HOTKEYS, ...JSON.parse(saved) };
    } catch {}
    return { ...DEFAULT_HOTKEYS };
}

/** What is on the board when it unmounts (recording stopped), so it survives into the next
 *  recording and the user can still save it. dataUrl: the strokes as a transparent PNG. */
export interface DrawingStash {
    json: string;
    dataUrl: string | null;
    pages: { dataUrl: string; fabricJson: string }[];
    dirty: boolean;
}

interface Props {
    onClose: () => void;
    visible: boolean;
    /** Resolves to false when the drawing could not be saved. fabricJson: the editable strokes. */
    onSaveToNote?: (dataUrl: string, fabricJson?: string) => void | boolean | Promise<void | boolean>;
    /** Drawings already attached to the open note, to bring back onto the canvas. */
    noteDrawings?: { id: string; content: string; vectorPath?: string }[];
    /** Fetches a drawing's saved strokes (fabric JSON) from Storage. */
    loadDrawingVector?: (path: string) => Promise<string | null>;
    /** Kept by the parent across unmounts (stopping a recording unmounts the board). */
    stash?: React.MutableRefObject<DrawingStash | null>;
    /** Told on unmount whether the board held strokes not yet saved to a note. */
    onStashed?: (unsaved: boolean) => void;
}

export function ScreenDrawingOverlay({ onClose, visible, onSaveToNote, noteDrawings = [], loadDrawingVector, stash, onStashed }: Props) {
    const canvasElRef = useRef<HTMLCanvasElement>(null);
    const fabricRef = useRef<any>(null);
    const visibleRef = useRef(visible);
    useEffect(() => { visibleRef.current = visible; }, [visible]);

    const [tool, setTool] = useState<Tool>('pen');
    const [color, setColor] = useState('#ef4444');
    const [customColors, setCustomColors] = useState<string[]>(loadCustomColors);
    const allColors = useMemo(() => [...COLORS.map(c => c.v), ...customColors], [customColors]);
    const allColorsRef = useRef(allColors);
    useEffect(() => { allColorsRef.current = allColors; }, [allColors]);
    useEffect(() => { try { localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(customColors)); } catch {} }, [customColors]);
    // The native "change" event fires once, when the picker is closed (React's onChange fires on
    // every drag of the picker) — that is when the colour joins the list. Bound through a callback
    // ref because the toolbar is not always mounted.
    const commitCustomColor = (raw: string) => {
        const v = raw.toLowerCase();
        setColor(v);
        setCustomColors(prev => (prev.includes(v) || COLORS.some(c => c.v === v) ? prev : [...prev, v].slice(-MAX_CUSTOM_COLORS)));
    };
    const commitCustomRef = useRef(commitCustomColor);
    commitCustomRef.current = commitCustomColor;
    const bindCustomInput = useCallback((el: HTMLInputElement | null) => {
        if (el && !el.dataset.bound) {
            el.dataset.bound = '1';
            el.addEventListener('change', () => commitCustomRef.current(el.value));
        }
    }, []);
    const [sizeIdx, setSizeIdx] = useState(1);
    const [bgColor, setBgColor] = useState<string | null>(null);
    // The solid background the toggle brings back, and the pattern drawn on it (both remembered).
    const [lastSolidBg, setLastSolidBg] = useState<string>(() => { try { return localStorage.getItem(LAST_BG_KEY) || '#ffffff'; } catch { return '#ffffff'; } });
    const [bgPattern, setBgPattern] = useState<BgPattern>(() => { try { return (localStorage.getItem(BG_PATTERN_KEY) as BgPattern) || 'none'; } catch { return 'none'; } });
    const bgPatternRef = useRef<BgPattern>(bgPattern);
    useEffect(() => { bgPatternRef.current = bgPattern; try { localStorage.setItem(BG_PATTERN_KEY, bgPattern); } catch {} }, [bgPattern]);
    useEffect(() => { try { localStorage.setItem(LAST_BG_KEY, lastSolidBg); } catch {} }, [lastSolidBg]);
    const chooseSolidBg = (v: string | null) => { setBgColor(v); if (v) setLastSolidBg(v); };
    // One click: clear ⇄ the last solid background.
    const toggleBackground = useCallback(() => {
        setBgColor(cur => (cur ? null : lastSolidBgRef.current));
    }, []);
    const lastSolidBgRef = useRef(lastSolidBg);
    lastSolidBgRef.current = lastSolidBg;
    const [showBgPanel, setShowBgPanel] = useState(false);
    const bgColorRef = useRef<string | null>(null);
    // past/present/future redo (docs/undo_redo_incident_20260922.md §5.3) — historyStack
    // holds the past (present is implicitly "whatever the live canvas is right now").
    const [historyStack, setHistoryStack] = useState<string[]>([]);
    const [futureStack, setFutureStack] = useState<string[]>([]);
    const [fabricReady, setFabricReady] = useState(false);
    const [hotkeys, setHotkeys] = useState<HotkeyMap>(loadHotkeys);
    const [showHotkeyPanel, setShowHotkeyPanel] = useState(false);
    const [remapping, setRemapping] = useState<HotkeyAction | null>(null);
    // Snapshot pages
    const [pages, setPages] = useState<{ dataUrl: string; fabricJson: string }[]>([]);
    const pagesRef = useRef<{ dataUrl: string; fabricJson: string }[]>([]);
    useEffect(() => { pagesForStashRef.current = pages; }, [pages]);

    // Refs so event handlers always read current value
    const toolRef = useRef<Tool>('pen');
    const colorRef = useRef('#ef4444');
    const sizeIdxRef = useRef(1);
    const historyRef = useRef<string[]>([]);
    const futureRef = useRef<string[]>([]);
    // Suppression flag: true from just before a programmatic loadFromJSON() (undo/redo/
    // restore) until its completion callback fires. fabric's loadFromJSON is callback-
    // based, not a promise, so the flag must stay up through that async completion, not
    // just around the synchronous call — otherwise objects it creates while deserializing
    // fire path:created/object:modified and leak a bogus snapshot into history.
    const isRestoringRef = useRef(false);
    // Unsaved-work tracking: every recorded change bumps changeSeq; a successful save to the note
    // remembers it. Strokes on the board with changeSeq ≠ savedSeq have not been saved yet.
    const changeSeqRef = useRef(0);
    const savedSeqRef = useRef(0);
    const hasUnsavedStrokes = () => {
        const fc = fabricRef.current;
        return !!fc && fc.getObjects().length > 0 && changeSeqRef.current !== savedSeqRef.current;
    };
    const hotkeysRef = useRef<HotkeyMap>(hotkeys);
    // Shift+Tab tool cycle index
    const toolCycleIdxRef = useRef(0);

    const selectTool = useCallback((t: Tool) => {
        const idx = TOOL_ORDER.indexOf(t);
        if (idx >= 0) toolCycleIdxRef.current = idx;
        setTool(t);
    }, []);
    const selectColor = useCallback((v: string) => {
        setColor(v);
    }, []);

    useEffect(() => { toolRef.current = tool; }, [tool]);
    useEffect(() => { colorRef.current = color; }, [color]);
    useEffect(() => { sizeIdxRef.current = sizeIdx; }, [sizeIdx]);
    useEffect(() => { historyRef.current = historyStack; }, [historyStack]);
    useEffect(() => { futureRef.current = futureStack; }, [futureStack]);
    useEffect(() => {
        hotkeysRef.current = hotkeys;
        try { localStorage.setItem('draw_hotkeys', JSON.stringify(hotkeys)); } catch {}
    }, [hotkeys]);
    useEffect(() => { pagesRef.current = pages; }, [pages]);
    useEffect(() => { bgColorRef.current = bgColor; }, [bgColor]);

    // ── Apply background color/pattern/transparency to the drawing canvas ─────
    // The background is a view setting, not part of the drawing's history: it is re-applied after
    // every clear, undo/redo and page restore (loadFromJSON would otherwise bring back whatever
    // background the snapshot was taken with).
    const applyBackground = useCallback((fc: any) => {
        if (!fc) return;
        const color = bgColorRef.current;
        const pattern = bgPatternRef.current;
        if (color && pattern !== 'none' && window.fabric?.Pattern) {
            fc.backgroundColor = new window.fabric.Pattern({ source: makePatternTile(color, pattern), repeat: 'repeat' });
        } else {
            fc.backgroundColor = color || '';
        }
        fc.renderAll();
    }, []);
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc || !fabricReady) return;
        bgColorRef.current = bgColor;
        bgPatternRef.current = bgPattern;
        applyBackground(fc);
    }, [bgColor, bgPattern, fabricReady, applyBackground]);

    // Draggable toolbar
    const [tbPos, setTbPos] = useState({ x: Math.max(0, window.innerWidth / 2 - 240), y: 90 });
    const onGripPointerDown = (e: React.PointerEvent) => {
        const origX = tbPos.x, origY = tbPos.y;
        const startX = e.clientX, startY = e.clientY;
        const onMove = (ev: PointerEvent) => setTbPos({ x: origX + ev.clientX - startX, y: origY + ev.clientY - startY });
        const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        e.preventDefault();
    };

    // ── Save/restore history — past/present/future (§5.3) ──────────────────────
    // historyStack's last entry IS "present"; everything before it is "past". A new
    // stroke drawn after an undo invalidates whatever was in futureStack, exactly like
    // any standard editor (Draw A, Draw B, Undo, Draw C → future cleared).
    const pushSnapshot = useCallback(() => {
        if (isRestoringRef.current) return; // don't record snapshots caused by our own restore
        const fc = fabricRef.current;
        if (!fc) return;
        const snap = JSON.stringify(fc.toJSON());
        setHistoryStack(h => [...h.slice(-49), snap]);
        setFutureStack([]);
        changeSeqRef.current++;
    }, []);

    // Load a fabric JSON snapshot onto the canvas, keeping the suppression flag up for
    // the FULL async lifecycle of loadFromJSON (it's callback-based, not a promise) so
    // objects created while deserializing never leak into history. Reset even on error.
    const loadSnapshotSuppressed = useCallback((json: string) => {
        const fc = fabricRef.current;
        if (!fc) return;
        isRestoringRef.current = true;
        // Draw nothing until the snapshot is fully loaded and the background re-applied: one render.
        fc.renderOnAddRemove = false;
        try {
            fc.loadFromJSON(parseWithoutBackground(json), () => {
                fc.renderOnAddRemove = true;
                applyBackground(fc);
                isRestoringRef.current = false;
            });
        } catch {
            fc.renderOnAddRemove = true;
            isRestoringRef.current = false;
        }
    }, [applyBackground]);

    const handleUndo = useCallback(() => {
        const h = historyRef.current;
        if (h.length <= 1) return;
        const current = h[h.length - 1];
        const prev = h[h.length - 2];
        setFutureStack(f => [...f, current]);
        setHistoryStack(h2 => h2.slice(0, -1));
        loadSnapshotSuppressed(prev);
    }, [loadSnapshotSuppressed]);

    const handleRedo = useCallback(() => {
        const f = futureRef.current;
        if (f.length === 0) return;
        const next = f[f.length - 1];
        setFutureStack(f2 => f2.slice(0, -1));
        setHistoryStack(h => [...h, next]);
        loadSnapshotSuppressed(next);
    }, [loadSnapshotSuppressed]);

    // ── Register with the centralized undo arbiter (§5.4) ──────────────────────
    // This component mounts once and stays mounted-but-hidden while toggled off (to
    // preserve the drawing), so isActive() — not mount/unmount — gates eligibility.
    // Re-activate (bump recency) each time it becomes visible again, so toggling it
    // back on while some other scope (e.g. an expanded note) is also active correctly
    // hands undo ownership back to whichever the user just brought to front.
    const undoScopeHandleRef = useRef<ReturnType<typeof registerUndoScope> | null>(null);
    const overlayRootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const handle = registerUndoScope({
            id: 'drawing-overlay',
            isActive: () => visibleRef.current,
            allowNativeEditableTargets: false,
            // The overlay covers the whole screen, so a caret still sitting in the note
            // underneath is not what the user is working in: Ctrl+Z undoes the drawing
            // instead of the note's text. The overlay's own inputs keep native undo.
            claimsEditableTarget: (el) => !overlayRootRef.current?.contains(el),
            undo: () => handleUndo(),
            redo: () => handleRedo(),
            getUndoLabel: () => null, // per-stroke undo — too noisy to toast on every step
            getRedoLabel: () => null,
        });
        undoScopeHandleRef.current = handle;
        handle.activate();
        return () => { undoScopeHandleRef.current = null; handle.unregister(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!visible) return;
        undoScopeHandleRef.current?.activate();
        // Take the caret out of whatever note field was being typed in, so neither Ctrl+Z
        // nor stray keys reach it while drawing.
        const active = document.activeElement as HTMLElement | null;
        if (active && active !== document.body && !overlayRootRef.current?.contains(active)) active.blur();
    }, [visible]);

    // ── Init Fabric ────────────────────────────────────────────────────────────
    const initCanvas = useCallback(() => {
        if (!canvasElRef.current || !window.fabric) return;
        const fc = new window.fabric.Canvas(canvasElRef.current, {
            isDrawingMode: true,
            selection: false,
            width: window.innerWidth,
            height: window.innerHeight,
            backgroundColor: null,
        });
        fabricRef.current = fc;

        const wrapper = canvasElRef.current.parentElement?.querySelector('.canvas-container') as HTMLElement | null;
        if (wrapper) { wrapper.style.background = 'transparent'; wrapper.style.position = 'absolute'; wrapper.style.inset = '0'; }

        fc.freeDrawingBrush = new window.fabric.PencilBrush(fc);
        fc.freeDrawingBrush.color = colorRef.current;
        fc.freeDrawingBrush.width = SIZES[sizeIdxRef.current];

        fc.on('path:created', pushSnapshot);
        fc.on('object:modified', pushSnapshot);

        pushSnapshot();
        savedSeqRef.current = changeSeqRef.current; // an empty board counts as saved
        // The board from the previous recording (stopping unmounts it): bring it back.
        const kept = stash?.current;
        if (kept) {
            stash!.current = null;
            if (kept.pages.length) setPages(kept.pages);
            if (kept.json) {
                isRestoringRef.current = true;
                try {
                    fc.loadFromJSON(parseWithoutBackground(kept.json), () => {
                        fc.renderAll();
                        isRestoringRef.current = false;
                        setHistoryStack([JSON.stringify(fc.toJSON())]);
                        setFutureStack([]);
                        if (kept.dirty) savedSeqRef.current = -1; // still not saved to a note
                    });
                } catch { isRestoringRef.current = false; }
            }
        }
        setFabricReady(true);
    }, [pushSnapshot]);

    // On unmount (the recording stopped), keep what is on the board for the parent. Declared before
    // the effect that disposes the canvas, so this cleanup runs first.
    const pagesForStashRef = useRef<{ dataUrl: string; fabricJson: string }[]>([]);
    useEffect(() => () => {
        const fc = fabricRef.current;
        if (!fc || !stash) return;
        const objects = fc.getObjects().length;
        let dataUrl: string | null = null;
        let json = '';
        if (objects > 0) {
            const shown = fc.backgroundColor;
            fc.backgroundColor = '';
            try { dataUrl = fc.toDataURL({ format: 'png', multiplier: 1 }); } catch { dataUrl = null; }
            fc.backgroundColor = shown;
            const data = fc.toJSON(); delete data.background; delete data.backgroundImage;
            json = JSON.stringify(data);
        }
        const dirty = objects > 0 && changeSeqRef.current !== savedSeqRef.current;
        if (objects > 0 || pagesForStashRef.current.length > 0) {
            stash.current = { json, dataUrl, pages: pagesForStashRef.current, dirty };
        }
        onStashed?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (window.fabric) { initCanvas(); return; }
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js';
        s.crossOrigin = 'anonymous';
        s.onload = initCanvas;
        document.head.appendChild(s);
        return () => { fabricRef.current?.dispose(); fabricRef.current = null; };
    }, []);

    // Resize
    useEffect(() => {
        const onResize = () => fabricRef.current?.setDimensions({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ── Update brush / mode when tool/color/size changes ──────────────────────
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc || !fabricReady) return;

        if (tool === 'pen') {
            fc.isDrawingMode = true;
            fc.selection = false;
            fc.freeDrawingBrush = new window.fabric.PencilBrush(fc);
            fc.freeDrawingBrush.color = color;
            fc.freeDrawingBrush.width = SIZES[sizeIdx];
        } else if (tool === 'select') {
            fc.isDrawingMode = false;
            fc.selection = true;
            fc.getObjects().forEach((o: any) => { o.selectable = true; o.evented = true; });
            fc.renderAll();
        } else if (tool === 'eraser') {
            fc.isDrawingMode = false;
            fc.selection = false;
            // evented=true so hit-detection works for erasure — except a picture brought back from the
            // note (the only images on this canvas), which stays as a locked backdrop.
            fc.getObjects().forEach((o: any) => { o.selectable = false; o.evented = o.type !== 'image'; });
            fc.renderAll();
        } else {
            fc.isDrawingMode = false;
            fc.selection = false;
            fc.getObjects().forEach((o: any) => { o.selectable = false; o.evented = false; });
            fc.renderAll();
        }
    }, [tool, color, sizeIdx, fabricReady]);

    // ── Shape & eraser mouse handlers ──────────────────────────────────────────
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc || !fabricReady) return;
        if (!['line', 'arrow', 'circle', 'eraser'].includes(tool)) return;

        let isDown = false;
        let sx = 0, sy = 0;
        let activeObj: any = null;

        // Eraser: hold and drag to wipe every stroke the pointer passes over. A stroke counts as hit
        // only where its ink is (not anywhere inside its bounding box), and the path between two mouse
        // events is sampled so a quick swipe does not skip thin strokes. One undo step per swipe.
        let erasing = false;
        let erasedAny = false;
        let last = { x: 0, y: 0 };
        const eraseAlong = (a: { x: number; y: number }, b: { x: number; y: number }) => {
            const r = Math.max(6, SIZES[sizeIdxRef.current]);
            const objs = fc.getObjects().filter((o: any) => o.evented && o.type !== 'image');
            if (!objs.length) return;
            const dist = Math.hypot(b.x - a.x, b.y - a.y);
            const steps = Math.max(1, Math.ceil(dist / Math.max(3, r / 2)));
            const prevTol = fc.targetFindTolerance;
            fc.targetFindTolerance = r;
            const gone = new Set<any>();
            for (let i = 0; i <= steps; i++) {
                const x = a.x + (b.x - a.x) * (i / steps), y = a.y + (b.y - a.y) * (i / steps);
                for (const o of objs) {
                    if (gone.has(o)) continue;
                    const bb = o.getBoundingRect();
                    if (x < bb.left - r || x > bb.left + bb.width + r || y < bb.top - r || y > bb.top + bb.height + r) continue;
                    if (!fc.isTargetTransparent(o, x, y)) { gone.add(o); fc.remove(o); }
                }
            }
            fc.targetFindTolerance = prevTol;
            if (gone.size) { erasedAny = true; fc.requestRenderAll(); }
        };

        const onDown = (opt: any) => {
            if (toolRef.current === 'eraser') {
                erasing = true; erasedAny = false;
                last = fc.getPointer(opt.e);
                eraseAlong(last, last);
                return;
            }
            const p = fc.getPointer(opt.e);
            isDown = true; sx = p.x; sy = p.y;
            const stroke = colorRef.current;
            const sw = SIZES[sizeIdxRef.current];

            if (toolRef.current === 'line' || toolRef.current === 'arrow') {
                activeObj = new window.fabric.Line([sx, sy, sx, sy], {
                    stroke, strokeWidth: sw, selectable: false, evented: false, strokeLineCap: 'round',
                });
                fc.add(activeObj);
            } else if (toolRef.current === 'circle') {
                activeObj = new window.fabric.Ellipse({
                    left: sx, top: sy, rx: 1, ry: 1,
                    stroke, strokeWidth: sw, fill: '', selectable: false, evented: false,
                });
                fc.add(activeObj);
            }
        };

        const onMove = (opt: any) => {
            if (erasing) {
                const p = fc.getPointer(opt.e);
                eraseAlong(last, p);
                last = p;
                return;
            }
            if (!isDown || !activeObj) return;
            const p = fc.getPointer(opt.e);
            const t = toolRef.current;
            if (t === 'line' || t === 'arrow') {
                activeObj.set({ x2: p.x, y2: p.y });
            } else if (t === 'circle') {
                activeObj.set({
                    left: Math.min(p.x, sx),
                    top: Math.min(p.y, sy),
                    rx: Math.abs(p.x - sx) / 2,
                    ry: Math.abs(p.y - sy) / 2,
                });
            }
            fc.renderAll();
        };

        const onUp = (opt: any) => {
            if (erasing) {
                erasing = false;
                if (erasedAny) pushSnapshot();
                return;
            }
            if (!isDown) return;
            isDown = false;
            if (toolRef.current === 'arrow' && activeObj) {
                const p = fc.getPointer(opt.e);
                const dx = p.x - sx, dy = p.y - sy;
                if (Math.sqrt(dx * dx + dy * dy) > 8) {
                    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                    const sw = SIZES[sizeIdxRef.current];
                    fc.add(new window.fabric.Triangle({
                        left: p.x, top: p.y,
                        width: sw * 5, height: sw * 5,
                        fill: colorRef.current,
                        angle: angle + 90,
                        originX: 'center', originY: 'center',
                        selectable: false, evented: false,
                    }));
                }
            }
            activeObj = null;
            pushSnapshot();
            fc.renderAll();
        };

        fc.on('mouse:down', onDown);
        fc.on('mouse:move', onMove);
        fc.on('mouse:up', onUp);
        return () => { fc.off('mouse:down', onDown); fc.off('mouse:move', onMove); fc.off('mouse:up', onUp); };
    }, [tool, fabricReady, pushSnapshot]);


    // ── Remap listener ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!remapping) return;
        const onKey = (e: KeyboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Escape') { setRemapping(null); return; }
            // Reject modifier-only keys
            if (['Control', 'Meta', 'Alt', 'Shift'].includes(e.key)) return;
            const newKey = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            setHotkeys(prev => ({ ...prev, [remapping]: newKey }));
            setRemapping(null);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [remapping]);

    // Clearing is undoable (it is recorded like any other change), and a board holding strokes not
    // yet saved to the note asks first — a stray click on the bin used to lose the drawing for good.
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [clearSaving, setClearSaving] = useState(false);
    const doClear = () => {
        const fc = fabricRef.current;
        if (!fc) return;
        fc.clear();
        applyBackground(fc);
        pushSnapshot();
        setShowClearConfirm(false);
    };
    const handleClear = () => {
        const fc = fabricRef.current;
        if (!fc || fc.getObjects().length === 0) return;
        if (onSaveToNote && hasUnsavedStrokes()) { setShowClearConfirm(true); setShowBgPanel(false); setShowHotkeyPanel(false); return; }
        doClear();
    };
    const saveThenClear = async () => {
        setClearSaving(true);
        const ok = await handleSaveToNote();
        setClearSaving(false);
        if (ok) doClear();
    };

    const handleSavePage = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc || fc.getObjects().length === 0) return;
        const fabricJson = JSON.stringify(fc.toJSON());
        const dataUrl = fc.toDataURL({ format: 'png', multiplier: 0.25 });
        setPages(prev => [...prev, { dataUrl, fabricJson }]);
        fc.clear(); applyBackground(fc);
        setHistoryStack([JSON.stringify(fc.toJSON())]);
        setFutureStack([]);
    }, []);

    // ── Save current drawing as an attachment on the note ──────────────────────
    const [savedFlash, setSavedFlash] = useState<false | 'ok' | 'failed'>(false);
    const handleSaveToNote = useCallback(async () => {
        const fc = fabricRef.current;
        if (!fc || !onSaveToNote || fc.getObjects().length === 0) return;
        // Only the strokes go to the note — never the solid background or pattern used while drawing.
        // toDataURL renders into an offscreen canvas, so swapping the background out and back for the
        // export never shows on screen.
        const shownBackground = fc.backgroundColor;
        fc.backgroundColor = '';
        let dataUrl: string;
        try { dataUrl = fc.toDataURL({ format: 'png', multiplier: 1 }); }
        finally { fc.backgroundColor = shownBackground; }
        // The strokes too, so the drawing can be reopened later with every stroke still editable.
        const strokes = fc.toJSON();
        delete strokes.background;
        delete strokes.backgroundImage;
        const fabricJson = JSON.stringify(strokes);
        // Show the tick only once the note really has the drawing.
        const seqAtSave = changeSeqRef.current;
        let ok = true;
        try { ok = (await onSaveToNote(dataUrl, fabricJson)) !== false; } catch { ok = false; }
        if (ok) savedSeqRef.current = seqAtSave;
        setSavedFlash(ok ? 'ok' : 'failed');
        setTimeout(() => setSavedFlash(false), ok ? 1500 : 4000);
        return ok;
    }, [onSaveToNote]);

    // ── Reopen a drawing saved to the note ──────────────────────────────────────
    // With saved strokes (vectorPath) every stroke comes back editable; an older drawing saved as a
    // picture only comes back as a locked backdrop image to draw over. Whatever is on the canvas is
    // kept in the pages strip first, so nothing is lost.
    const [showOpenPanel, setShowOpenPanel] = useState(false);
    const [openingId, setOpeningId] = useState<string | null>(null);
    const handleOpenDrawing = useCallback(async (d: { id: string; content: string; vectorPath?: string }) => {
        const fc = fabricRef.current;
        if (!fc) return;
        setOpeningId(d.id);
        try {
            if (fc.getObjects().length > 0) {
                const currentJson = JSON.stringify(fc.toJSON());
                const currentDataUrl = fc.toDataURL({ format: 'png', multiplier: 0.25 });
                setPages(prev => [...prev, { dataUrl: currentDataUrl, fabricJson: currentJson }]);
            }
            const json = d.vectorPath && loadDrawingVector ? await loadDrawingVector(d.vectorPath).catch(() => null) : null;
            if (json) {
                isRestoringRef.current = true;
                await new Promise<void>(resolve => {
                    fc.renderOnAddRemove = false;
                    try { fc.loadFromJSON(parseWithoutBackground(json), () => { fc.renderOnAddRemove = true; applyBackground(fc); resolve(); }); }
                    catch { fc.renderOnAddRemove = true; resolve(); }
                });
                isRestoringRef.current = false;
                setHistoryStack([JSON.stringify(fc.toJSON())]);
                setFutureStack([]);
            } else {
                await new Promise<void>(resolve => {
                    window.fabric.Image.fromURL(d.content, (img: any) => {
                        if (!img || !img.width) { resolve(); return; }
                        const scale = Math.min((fc.getWidth() * 0.9) / img.width, (fc.getHeight() * 0.9) / img.height, 1);
                        img.set({
                            scaleX: scale, scaleY: scale,
                            left: (fc.getWidth() - img.width * scale) / 2,
                            top: (fc.getHeight() - img.height * scale) / 2,
                            selectable: toolRef.current === 'select', evented: toolRef.current === 'select',
                        });
                        isRestoringRef.current = true;
                        fc.clear();
                        fc.add(img);
                        fc.sendToBack(img);
                        applyBackground(fc);
                        isRestoringRef.current = false;
                        setHistoryStack([JSON.stringify(fc.toJSON())]);
                        setFutureStack([]);
                        resolve();
                    }, { crossOrigin: 'anonymous' });
                });
            }
            setShowOpenPanel(false);
        } finally {
            setOpeningId(null);
        }
    }, [loadDrawingVector, applyBackground]);

    // ── Keyboard hotkeys ───────────────────────────────────────────────────────
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            // Overlay is hidden (toggled off but still mounted to preserve the drawing) — let all
            // keys pass through to the rest of the app. The parent owns showing/hiding via its own
            // Shift+toggleOverlay listener.
            if (!visibleRef.current) return;

            // Undo/redo (Ctrl+Z, Ctrl+Shift+Z, Ctrl+Y) is handled by the centralized undo
            // arbiter (hooks/useUndoArbiter.ts, §5.4) via the registration below, not here
            // — handling it in both places would reproduce this incident's original bug
            // class of two listeners racing the same keypress (§5.4.2).

            // Skip if any non-shift modifier held, or if user is typing in an input
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            // Shift+Tab: cycle through tools only (forward each press)
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                toolCycleIdxRef.current = (toolCycleIdxRef.current + 1) % TOOL_ORDER.length;
                setTool(TOOL_ORDER[toolCycleIdxRef.current]);
                return;
            }

            const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            const hk = hotkeysRef.current;

            // Tool hotkeys
            const toolActions: HotkeyAction[] = ['pen', 'eraser', 'line', 'arrow', 'circle', 'select'];
            for (const action of toolActions) {
                if (hk[action] && key === hk[action].toLowerCase()) {
                    e.preventDefault();
                    selectTool(action as Tool);
                    return;
                }
            }

            // Color cycling via hotkey
            if (hk.colorNext && key === hk.colorNext.toLowerCase()) {
                e.preventDefault();
                setColor(prev => {
                    const list = allColorsRef.current;
                    return list[(list.indexOf(prev) + 1) % list.length];
                });
                return;
            }
            if (hk.colorPrev && key === hk.colorPrev.toLowerCase()) {
                e.preventDefault();
                setColor(prev => {
                    const list = allColorsRef.current;
                    const idx = list.indexOf(prev);
                    return list[((idx < 0 ? 0 : idx) - 1 + list.length) % list.length];
                });
                return;
            }

            // Size
            if (hk.sizeUp && key === hk.sizeUp.toLowerCase()) {
                e.preventDefault();
                setSizeIdx(i => Math.min(i + 1, SIZES.length - 1));
                return;
            }
            if (hk.sizeDown && key === hk.sizeDown.toLowerCase()) {
                e.preventDefault();
                setSizeIdx(i => Math.max(i - 1, 0));
                return;
            }

            // Save & new page
            if (hk.savePage && key === hk.savePage.toLowerCase()) {
                e.preventDefault();
                handleSavePage();
                return;
            }
            // Solid ⇄ clear background
            if (hk.toggleBg && key === hk.toggleBg.toLowerCase()) {
                e.preventDefault();
                toggleBackground();
                return;
            }
        };
        // 🐛 FIX: must be capture-phase, not bubble. App.tsx's global Ctrl+Z handler
        // (window keydown, bubble phase, registered earlier on app mount) ran BEFORE this
        // listener and had no stopPropagation — so one Cmd+Z during recording undid the
        // drawn stroke here AND reverted the whole notebook's nodes array via App.tsx's
        // undo(), silently wiping notes. Capture phase always fires before any bubble-phase
        // listener regardless of mount order, so stopPropagation() below now reliably stops
        // App.tsx's handler from ever running while this overlay is visible.
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [handleSavePage]);

    const handleRestorePage = useCallback((idx: number) => {
        const fc = fabricRef.current;
        if (!fc) return;
        const page = pagesRef.current[idx];
        if (!page) return;
        // Snapshot current canvas if non-empty, push back to pages (swap)
        const currentObjs = fc.getObjects().length;
        const currentJson = JSON.stringify(fc.toJSON());
        const currentDataUrl = currentObjs > 0 ? fc.toDataURL({ format: 'png', multiplier: 0.25 }) : null;
        // Load selected page
        isRestoringRef.current = true;
        fc.renderOnAddRemove = false;
        try {
            fc.loadFromJSON(parseWithoutBackground(page.fabricJson), () => {
                fc.renderOnAddRemove = true;
                applyBackground(fc);
                isRestoringRef.current = false;
            });
        } catch {
            fc.renderOnAddRemove = true;
            isRestoringRef.current = false;
        }
        setHistoryStack([page.fabricJson]);
        setFutureStack([]);
        setPages(prev => {
            const next = prev.filter((_, i) => i !== idx);
            if (currentObjs > 0 && currentDataUrl) next.push({ dataUrl: currentDataUrl, fabricJson: currentJson });
            return next;
        });
    }, []);

    const handleDeletePage = useCallback((idx: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setPages(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const canUndo = historyStack.length > 1;
    const canRedo = futureStack.length > 0;

    const TOOLS: { id: Tool; icon: React.ReactNode; title: string }[] = [
        { id: 'pen',    icon: <Pen size={14} />,            title: 'Pen' },
        { id: 'eraser', icon: <Eraser size={14} />,         title: 'Eraser' },
        { id: 'line',   icon: <Minus size={14} />,          title: 'Line' },
        { id: 'arrow',  icon: <ArrowRight size={14} />,     title: 'Arrow' },
        { id: 'circle', icon: <Circle size={14} />,         title: 'Circle' },
        { id: 'select', icon: <MousePointer2 size={14} />,  title: 'Select' },
    ];

    const toolHotkeyActions: HotkeyAction[] = ['pen', 'eraser', 'line', 'arrow', 'circle', 'select'];
    const otherHotkeyActions: HotkeyAction[] = ['colorNext', 'colorPrev', 'sizeUp', 'sizeDown', 'savePage', 'toggleBg'];
    // toggleOverlay is Ctrl+key — configured pre-recording, not shown here

    const cursor = tool === 'select' ? 'default' : tool === 'eraser' ? 'cell' : 'crosshair';
    const wrapperBackground = useMemo<React.CSSProperties>(() => {
        if (!bgColor) return {};
        return bgPattern === 'none'
            ? { backgroundColor: bgColor }
            : { backgroundColor: bgColor, backgroundImage: `url(${makePatternTile(bgColor, bgPattern).toDataURL()})` };
    }, [bgColor, bgPattern]);

    return (
        <div ref={overlayRootRef} className="fixed inset-0 z-[10000] pointer-events-none" style={{ display: visible ? undefined : 'none' }}>
            {/* Transparent drawing canvas */}
            {/* The wrapper carries the same solid background as the canvas, so a frame in which the
                canvas is momentarily blank (while a snapshot loads) never shows the screen below. */}
            <div className="absolute inset-0 pointer-events-auto" style={{ cursor, ...wrapperBackground }}>
                <canvas ref={canvasElRef} style={{ display: 'block' }} />
            </div>

            {/* Floating toolbar */}
            <div
                className="absolute pointer-events-auto flex items-center gap-1 px-2.5 py-2 bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 select-none"
                style={{ left: tbPos.x, top: tbPos.y, zIndex: 1 }}
            >
                {/* Drag grip */}
                <div
                    className="cursor-grab active:cursor-grabbing p-1 text-gray-500 hover:text-gray-300 transition-colors"
                    onPointerDown={onGripPointerDown}
                    title="Drag to move toolbar"
                >
                    <Move size={13} />
                </div>

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                {/* Tool buttons */}
                {TOOLS.map(t => {
                    const hkAction = t.id as HotkeyAction;
                    const hkLabel = hotkeys[hkAction];
                    return (
                        <button
                            key={t.id}
                            onClick={() => selectTool(t.id)}
                            title={`${t.title} (${hkLabel?.toUpperCase() ?? '—'})`}
                            className={`relative p-1.5 rounded-lg transition-all ${tool === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                        >
                            {t.icon}
                            {hkLabel && (
                                <span className={`absolute -bottom-0.5 -right-0.5 text-[7px] font-bold leading-none px-0.5 rounded ${tool === t.id ? 'bg-gray-900 text-white' : 'bg-white/15 text-gray-400'}`}>
                                    {hkLabel.toUpperCase()}
                                </span>
                            )}
                        </button>
                    );
                })}

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                {/* Color swatches */}
                {COLORS.map(c => (
                    <button
                        key={c.v}
                        onClick={() => selectColor(c.v)}
                        title={c.label}
                        className={`w-4 h-4 rounded-full transition-all border-2 ${color === c.v ? 'border-white scale-125' : 'border-white/20 hover:border-white/60'}`}
                        style={{ backgroundColor: c.v }}
                    />
                ))}
                {customColors.map(v => (
                    <button
                        key={v}
                        onClick={() => selectColor(v)}
                        onContextMenu={e => { e.preventDefault(); setCustomColors(prev => prev.filter(x => x !== v)); }}
                        title="Custom colour — right-click to remove"
                        className={`w-4 h-4 rounded-full transition-all border-2 ${color === v ? 'border-white scale-125' : 'border-white/20 hover:border-white/60'}`}
                        style={{ backgroundColor: v }}
                    />
                ))}
                <label
                    title="Add a custom colour"
                    className="relative w-4 h-4 rounded-full border-2 border-dashed border-white/40 hover:border-white text-white/70 hover:text-white flex items-center justify-center cursor-pointer text-[10px] leading-none"
                >
                    +
                    <input
                        ref={bindCustomInput}
                        type="color"
                        defaultValue="#06b6d4"
                        onChange={e => setColor(e.target.value.toLowerCase())}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                </label>

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                {/* Stroke size */}
                {SIZES.map((s, i) => (
                    <button
                        key={i}
                        onClick={() => setSizeIdx(i)}
                        title={`Size ${i + 1}`}
                        className={`flex items-center justify-center w-6 h-6 rounded-lg transition-colors ${sizeIdx === i ? 'bg-white/20' : 'hover:bg-white/10'}`}
                    >
                        <div className="rounded-full bg-white" style={{ width: s, height: s }} />
                    </button>
                ))}

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                <button
                    onClick={handleUndo}
                    disabled={!canUndo}
                    title="Undo (Ctrl+Z)"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
                >
                    <Undo2 size={14} />
                </button>
                <button
                    onClick={handleRedo}
                    disabled={!canRedo}
                    title="Redo (Ctrl+Shift+Z)"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
                >
                    <Redo2 size={14} />
                </button>
                <button
                    onClick={handleClear}
                    title="Clear all drawings"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/10 transition-colors"
                >
                    <Trash2 size={14} />
                </button>
                <button
                    onClick={handleSavePage}
                    title={`Save drawing & start new page (${(hotkeys.savePage || 'N').toUpperCase()})`}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-white/10 transition-colors"
                >
                    <Camera size={14} />
                </button>
                {noteDrawings.length > 0 && (
                    <button
                        onClick={() => { setShowOpenPanel(v => !v); setShowBgPanel(false); setShowHotkeyPanel(false); }}
                        title="Open a drawing from the note"
                        className={`p-1.5 rounded-lg transition-colors ${showOpenPanel ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                    >
                        <FolderOpen size={14} />
                    </button>
                )}
                {onSaveToNote && (
                    <button
                        onClick={handleSaveToNote}
                        title="Save this drawing to the note as an attachment"
                        className={`p-1.5 rounded-lg transition-colors ${savedFlash === 'ok' ? 'text-green-400' : savedFlash === 'failed' ? 'text-red-400' : 'text-gray-400 hover:text-green-400 hover:bg-white/10'}`}
                    >
                        {savedFlash === 'ok' ? <Check size={14} /> : <ImagePlus size={14} />}
                    </button>
                )}

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                {/* Background: click = solid ⇄ clear; the chevron opens colours & patterns */}
                <div className="flex items-center">
                    <button
                        onClick={toggleBackground}
                        title={`${bgColor ? 'Clear background (see the screen)' : 'Solid background'} (${(hotkeys.toggleBg || 'b').toUpperCase()})`}
                        className={`relative p-1.5 rounded-l-lg transition-colors ${bgColor ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                    >
                        <PaintBucket size={14} />
                        <span
                            className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white/40"
                            style={{ backgroundColor: bgColor || lastSolidBg, opacity: bgColor ? 1 : 0.45 }}
                        />
                    </button>
                    <button
                        onClick={() => { setShowBgPanel(v => !v); setShowHotkeyPanel(false); }}
                        title="Background colour and pattern"
                        className={`px-0.5 py-1.5 rounded-r-lg transition-colors ${showBgPanel ? 'bg-indigo-500 text-white' : 'text-gray-500 hover:text-white hover:bg-white/10'}`}
                    >
                        <ChevronDown size={10} />
                    </button>
                </div>

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                {/* Hotkey config button */}
                <button
                    onClick={() => { setShowHotkeyPanel(v => !v); setRemapping(null); setShowBgPanel(false); }}
                    title="Configure hotkeys"
                    className={`p-1.5 rounded-lg transition-colors ${showHotkeyPanel ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                >
                    <Keyboard size={14} />
                </button>

                <div className="w-px h-5 bg-white/10 mx-0.5" />

                <button
                    onClick={onClose}
                    title="Close drawing overlay"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/10 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>

            {/* Hotkey config panel */}
            {showHotkeyPanel && (
                <div
                    className="absolute pointer-events-auto bg-gray-900/98 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 p-3 w-56"
                    style={{ left: tbPos.x, top: tbPos.y + 52, zIndex: 1 }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold text-gray-300">Hotkeys</span>
                        <div className="flex items-center gap-1">
                            <span className="text-[9px] text-gray-500">⇧Tab = cycle tools</span>
                            <button
                                onClick={() => { setHotkeys({ ...DEFAULT_HOTKEYS }); setRemapping(null); }}
                                className="text-[9px] text-indigo-400 hover:text-indigo-300 ml-2"
                                title="Reset to defaults"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    <div className="text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Tools</div>
                    <div className="flex flex-col gap-1 mb-3">
                        {toolHotkeyActions.map(action => (
                            <HotkeyRow
                                key={action}
                                label={ACTION_LABELS[action]}
                                keyLabel={hotkeys[action]}
                                isRemapping={remapping === action}
                                onStartRemap={() => setRemapping(action)}
                            />
                        ))}
                    </div>

                    <div className="text-[10px] text-gray-500 mb-1.5 uppercase tracking-wide">Other</div>
                    <div className="flex flex-col gap-1">
                        {otherHotkeyActions.map(action => (
                            <HotkeyRow
                                key={action}
                                label={ACTION_LABELS[action]}
                                keyLabel={hotkeys[action]}
                                isRemapping={remapping === action}
                                onStartRemap={() => setRemapping(action)}
                            />
                        ))}
                    </div>

                    {remapping && (
                        <div className="mt-2 text-[10px] text-amber-400 text-center animate-pulse">
                            Press any key… (Esc to cancel)
                        </div>
                    )}
                </div>
            )}

            {/* Background color panel */}
            {showClearConfirm && (
                <div
                    className="absolute pointer-events-auto bg-gray-900 rounded-2xl shadow-2xl border border-white/10 p-3 w-72"
                    style={{ left: tbPos.x, top: tbPos.y + 52, zIndex: 1 }}
                >
                    <div className="text-[13px] text-white font-semibold mb-1">Hình vẽ này chưa lưu vào note</div>
                    <div className="text-[11px] text-gray-400 mb-3">Lưu lại trước khi xoá bảng?</div>
                    <div className="flex flex-col gap-1.5">
                        <button onClick={saveThenClear} disabled={clearSaving}
                            className="w-full px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white text-[12px] font-semibold">
                            {clearSaving ? 'Đang lưu…' : 'Lưu vào note rồi xoá'}
                        </button>
                        <button onClick={doClear}
                            className="w-full px-3 py-1.5 rounded-lg bg-white/10 hover:bg-red-500/80 text-white text-[12px]">
                            Xoá luôn (Ctrl+Z để lấy lại)
                        </button>
                        <button onClick={() => setShowClearConfirm(false)}
                            className="w-full px-3 py-1.5 rounded-lg text-gray-400 hover:text-white text-[12px]">
                            Huỷ
                        </button>
                    </div>
                </div>
            )}

            {showOpenPanel && (
                <div
                    className="absolute pointer-events-auto bg-gray-900 rounded-2xl shadow-2xl border border-white/10 p-3 w-72"
                    style={{ left: tbPos.x, top: tbPos.y + 52, zIndex: 1 }}
                >
                    <div className="text-[10px] text-gray-400 mb-2 uppercase tracking-wide">Drawings in this note</div>
                    <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                        {[...noteDrawings].reverse().map(d => (
                            <button
                                key={d.id}
                                onClick={() => handleOpenDrawing(d)}
                                disabled={!!openingId}
                                title={d.vectorPath ? 'Opens with every stroke editable' : 'Opens as a picture to draw over'}
                                className="relative rounded-lg overflow-hidden border-2 border-white/15 hover:border-indigo-400 bg-slate-800 aspect-video disabled:opacity-50"
                            >
                                <img src={d.content} alt="" className="w-full h-full object-contain" />
                                <span className="absolute bottom-1 right-1 text-[9px] px-1 rounded bg-black/60 text-white/80">{d.vectorPath ? 'editable' : 'picture'}</span>
                                {openingId === d.id && <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-[11px] text-white">…</span>}
                            </button>
                        ))}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-2">What is on the canvas now is kept in the pages strip on the right.</div>
                </div>
            )}

            {showBgPanel && (
                <div
                    className="absolute pointer-events-auto bg-gray-900/98 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 p-3 w-56"
                    style={{ left: tbPos.x, top: tbPos.y + 52, zIndex: 1 }}
                >
                    <div className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide">Background</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {BG_COLORS.map(c => (
                            <button
                                key={c.label}
                                onClick={() => chooseSolidBg(c.v)}
                                title={c.label}
                                className={`w-7 h-7 rounded-lg transition-all border-2 flex items-center justify-center ${bgColor === c.v ? 'border-indigo-400 scale-110' : 'border-white/20 hover:border-white/60'}`}
                                style={c.v ? { backgroundColor: c.v } : {
                                    backgroundImage: 'linear-gradient(45deg, #4b5563 25%, transparent 25%), linear-gradient(-45deg, #4b5563 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #4b5563 75%), linear-gradient(-45deg, transparent 75%, #4b5563 75%)',
                                    backgroundSize: '8px 8px',
                                    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                                    backgroundColor: '#1f2937',
                                }}
                            />
                        ))}
                    </div>
                    <label className="flex items-center gap-2 text-[11px] text-gray-400 cursor-pointer">
                        <input
                            type="color"
                            value={bgColor || lastSolidBg}
                            onChange={e => chooseSolidBg(e.target.value)}
                            className="w-7 h-7 rounded-lg border-2 border-white/20 bg-transparent cursor-pointer"
                        />
                        Custom color
                    </label>
                    <div className="text-[10px] text-gray-500 mt-3 mb-1.5 uppercase tracking-wide">Pattern</div>
                    <div className="flex gap-1.5">
                        {BG_PATTERNS.map(p => (
                            <button
                                key={p.v}
                                onClick={() => { setBgPattern(p.v); if (!bgColor) setBgColor(lastSolidBg); }}
                                title={p.label}
                                className={`flex-1 h-9 rounded-lg border-2 transition-all ${bgPattern === p.v ? 'border-indigo-400' : 'border-white/20 hover:border-white/60'}`}
                                style={{
                                    backgroundColor: bgColor || lastSolidBg,
                                    backgroundImage: p.v === 'none' ? undefined : `url(${makePatternTile(bgColor || lastSolidBg, p.v).toDataURL()})`,
                                }}
                            />
                        ))}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-2">Click the bucket (or press {(hotkeys.toggleBg || 'b').toUpperCase()}) to switch between this background and a clear one.</div>
                </div>
            )}

            {/* Saved pages strip — docked to the right edge, independent of toolbar position */}
            {pages.length > 0 && (
                <div
                    className="fixed pointer-events-auto right-3 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 max-h-[70vh] overflow-y-auto px-1.5 py-2 bg-gray-900/90 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl"
                    style={{ zIndex: 1 }}
                >
                    <span className="text-[8px] text-gray-500 uppercase tracking-wide text-center mb-0.5">Pages</span>
                    {pages.map((page, idx) => (
                        <div
                            key={idx}
                            className="relative group cursor-pointer flex-shrink-0"
                            onClick={() => handleRestorePage(idx)}
                            title={`Restore page ${idx + 1}`}
                        >
                            <img
                                src={page.dataUrl}
                                alt={`Page ${idx + 1}`}
                                className="w-14 h-8 object-contain rounded-lg border-2 border-white/20 group-hover:border-amber-400 transition-all bg-black/30"
                            />
                            <span className="absolute bottom-0.5 left-1 text-[7px] text-gray-400 group-hover:text-white transition-colors font-mono">{idx + 1}</span>
                            <button
                                onClick={(e) => handleDeletePage(idx, e)}
                                className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-gray-700 hover:bg-red-500 rounded-full text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow"
                                title="Delete page"
                            >
                                <X size={7} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function HotkeyRow({ label, keyLabel, isRemapping, onStartRemap }: {
    label: string;
    keyLabel: string;
    isRemapping: boolean;
    onStartRemap: () => void;
}) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">{label}</span>
            <button
                onClick={onStartRemap}
                className={`min-w-[28px] px-1.5 py-0.5 rounded text-[10px] font-mono font-bold text-center transition-colors ${
                    isRemapping
                        ? 'bg-amber-500 text-gray-900 animate-pulse'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                }`}
                title="Click to remap"
            >
                {isRemapping ? '…' : (keyLabel?.toUpperCase() || '—')}
            </button>
        </div>
    );
}
